import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MICE_DELETABLE_STATUSES,
  MICE_IN_FLIGHT_STATUSES,
  type MiceOrderStatus,
  type MiceProjectView,
} from '@voll-intelligence/domain';
import { queryKeys } from '@web/core/query';
import { miceRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import { useOrgContext, useSnackbar } from '@web/ui/core';

export const REFETCH_MS = 10_000;

export const canDeleteProject = (project: MiceProjectView): boolean =>
  Object.entries(project.counts).every(
    ([status, count]) => count === 0 || MICE_DELETABLE_STATUSES.includes(status as MiceOrderStatus),
  );

const hasInFlight = (project: MiceProjectView | undefined): boolean =>
  project ? MICE_IN_FLIGHT_STATUSES.some((s) => project.counts[s] > 0) : false;

export const errorMessage = (err: unknown): string =>
  err instanceof Error ? err.message : t('mice.error.generic');

export function useMiceOrderVouchers(projectId: string, orderId: string | null) {
  const { orgId } = useOrgContext();
  return useQuery({
    queryKey: queryKeys.mice.orderVouchers(orgId, projectId, orderId ?? ''),
    queryFn: () => miceRepository.listOrderVouchers(orgId, projectId, orderId ?? ''),
    enabled: orderId !== null,
  });
}

/** Projeto (cabeçalho e contadores), atualizado enquanto houver pedido em voo. */
export function useMiceProject(projectId: string) {
  const { orgId } = useOrgContext();
  return useQuery({
    queryKey: queryKeys.mice.project(orgId, projectId),
    queryFn: () => miceRepository.getProject(orgId, projectId),
    refetchInterval: (q) => (hasInFlight(q.state.data) ? REFETCH_MS : false),
    refetchIntervalInBackground: false,
  });
}

/** Apaga o evento e volta para a lista de projetos. */
export function useDeleteMiceProject(projectId: string) {
  const { orgId, orgSlug } = useOrgContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: () => miceRepository.deleteProject(orgId, projectId),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: queryKeys.mice.project(orgId, projectId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.mice.projects(orgId) });
      showSnackbar({ severity: 'success', message: t('mice.project.delete.done') });
      await navigate({ to: '/orgs/$orgSlug/mice', params: { orgSlug } });
    },
    onError: (err) => {
      showSnackbar({ severity: 'error', message: errorMessage(err) });
    },
    meta: { silent: true },
  });
}
